import React, { useCallback, useEffect, useState } from "react";
import { Button, Tooltip, Typography } from "antd";
import { withTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { compose } from "redux";
import { connect, useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import styled from "styled-components";

import { AllPatientsTable } from "./TableSection/AllPatientsTable";
import ACTIONS from "../store/action";
import { decodeNumbersForSearch } from "../utils/formaters";
import { GRAY_500, GRAY_900, WHITE } from "../constant/colors";
import { fontWeights, sizes } from "../constant/styles";
import ArrowBack from "../assets/svg/ArrowBack";
import { Routes } from "../constant/routes";
import { useHistory } from "react-router";
import {
  selectFilterList,
  selectPreviousPage,
  selectRecentPatientPage,
  selectShouldReturnToRelevantPatient
} from "../store/selector";

const { Text } = Typography;

const PHONE = "phone_number";
const SSN = "social_security_number";

const ContainerStyle = styled.div`
  margin: 0px 15px 0px 15px;
  position: relative;
  background: ${WHITE};
  border-radius: 10px;
  padding: 10px 15px 0px 15px;
`;

const BackButtonStyle = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0px;
`;

const SearchResultLblStyle = styled(Text)`
  font-size: ${sizes.medium_xlarge};
  font-weight: ${fontWeights.semibold};
  color: ${GRAY_900};
`;

const SearchResultValueStyle = styled(Text)`
  font-size: ${sizes.medium_xlarge};
  font-weight: ${fontWeights.regular};
  color: ${GRAY_500};
  margin-left: 5px;
`;

const formatSearchQuery = (searchText, searchBy) =>
  searchBy === SSN || searchBy === PHONE ? decodeNumbersForSearch(searchText) : searchText;

const SearchResultsComponent = ({ t, searchPatientAct }) => {
  const { search, state } = useLocation();
  const searchText = state?.searchText;
  const [startSearch, setStartSearch] = useState(false);

  const searchParams = new URLSearchParams(search);
  const searchBy = searchParams.get("searchBy");

  const dispatch = useDispatch();
  const history = useHistory();

  const savedFilter = useSelector(selectFilterList);
  const previousPage = useSelector(selectPreviousPage);

  const shouldReturnToRelevantPatientAct = (shouldReturn, notificationId) =>
    dispatch(ACTIONS.setShouldReturnToRelevantPatient(shouldReturn, notificationId));
  const shouldReturnToRelevantPatient = useSelector(selectShouldReturnToRelevantPatient);

  const { notificationId: topOpportunitiesNotificationId } = shouldReturnToRelevantPatient;

  const recentPatientPage = useSelector(selectRecentPatientPage);

  useEffect(() => {
    if (searchBy && searchText) {
      debouncedSearch(1, searchText, searchBy);
    } else if (!searchText) {
      history.push(`${Routes.RECENT_PATIENTS}`);
    }
  }, [searchText, searchBy]);

  const debouncedSearch = useCallback(
    debounce((page, searchText, searchBy, sorter) => {
      setStartSearch(true);
      searchPatientAct(page, formatSearchQuery(searchText, searchBy), searchBy, sorter);
    }, 500),
    []
  );

  const handleSortOrPagination = useCallback(
    (page, sorter) => {
      setStartSearch(true);
      searchPatientAct(page, formatSearchQuery(searchText, searchBy), searchBy, sorter);
    },
    [searchText, searchBy, searchPatientAct]
  );

  return (
    <ContainerStyle>
      <Tooltip title={t("back")}>
        <BackButtonStyle
          type="text"
          onClick={() => {
            if (shouldReturnToRelevantPatient.shouldReturn) {
              history.push(Routes.ROOT + `${Routes.TOP_OPPORTUNITIES}${topOpportunitiesNotificationId}`, {
                notification: topOpportunitiesNotificationId
              });
              shouldReturnToRelevantPatientAct(false, null);
            } else if (savedFilter?.id) {
              history.push(`${Routes.FILTER_LIST}/${savedFilter.id}`);
            } else if (recentPatientPage?.wasRecentPatientPage) {
              history.push(Routes.RECENT_PATIENTS);
            } else {
              // return to the last page that opened this link
              history.push(
                previousPage?.pathname
                  ? `${previousPage.pathname}${previousPage.search}${previousPage.hash}`
                  : Routes.ROOT,
                previousPage?.pathname ? previousPage.state : null
              );
            }
          }}
        >
          <ArrowBack />
        </BackButtonStyle>
      </Tooltip>

      <div style={{ paddingBottom: "15px" }}>
        <SearchResultLblStyle>{t("search_results")}:</SearchResultLblStyle>
        <SearchResultValueStyle id="patient_name_search_results">{searchText}</SearchResultValueStyle>
      </div>

      <AllPatientsTable startSearch={startSearch} isSearch={true} fetchOrders={handleSortOrPagination} />
    </ContainerStyle>
  );
};

const mapDispatchToProps = (dispatch) => ({
  searchPatientAct: (page, searchText, searchBy, sorter) =>
    dispatch(ACTIONS.searchPatientsAction(page, searchText, searchBy, sorter))
});

const SearchResults = compose(withTranslation(), connect(null, mapDispatchToProps))(SearchResultsComponent);
export { SearchResults };
